import { OrganizationsApi } from '@evimo/client/apis';
import { Configuration } from '@evimo/client';
import { CreateOrganizationRequestDto } from '@evimo/types/api/organization/create-organization/create-organization.request.dto';
import { UpdateOrganizationResponseDto } from '@evimo/types/api/organization/update-organization/update-organization.response.dto';
import { CreateOrganizationResponseDto } from '@evimo/types/api/organization/create-organization/create-organization.response.dto';
import { FindOrganizationDto } from '@evimo/types/api/organization/find-organization/find-organization.dto';
import { UpdateOrganizationRequestDto } from '@evimo/types/api/organization/update-organization/update-organization.request.dto';
import type {
  ListOrganizationsParams,
  ListOrganizationsResult,
} from '~/app/services/organization/organization.types';
import { KNOWLEDGE_API_URL } from '~/src/constants/env';
import { normalizeExternalCsmsConfig } from '~/app/components/organization/organizationExternalCsms.utils';

const API_BASE = KNOWLEDGE_API_URL;

const api = new OrganizationsApi(
  new Configuration({
    basePath: API_BASE,
    credentials: 'include',
  }),
);

export const OrganizationService = {
  async listOrganizations(
    params: ListOrganizationsParams,
  ): Promise<ListOrganizationsResult> {
    return api.organizationControllerFindAllOrganizations(
      {
        page: params.page,
        limit: params.limit,
        ...(params.q?.trim() ? { q: params.q.trim() } : {}),
      },
      { credentials: 'include' },
    ) as Promise<ListOrganizationsResult>;
  },

  async findAllOrganizations(): Promise<FindOrganizationDto[]> {
    const firstPage = await this.listOrganizations({
      page: 1,
      limit: 1000,
    });

    return firstPage.data;
  },

  /**
   * Returns only organizations whose organizationType is Manufacturer.
   */
  async findManufacturerOrganizations(): Promise<FindOrganizationDto[]> {
    const organizations = await this.findAllOrganizations();
    return organizations
      .filter(
        (organization) =>
          String(organization.organizationType) === 'Manufacturer',
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  async findOrganizationsBySearch(q: string): Promise<FindOrganizationDto[]> {
    const result = await this.listOrganizations({
      page: 1,
      limit: 100,
      q,
    });

    return result.data;
  },

  /**
   * Search organizations and keep only Manufacturer type results.
   */
  async findManufacturerOrganizationsBySearch(
    q: string,
  ): Promise<FindOrganizationDto[]> {
    const organizations = await this.findOrganizationsBySearch(q);
    return organizations.filter(
      (organization) =>
        String(organization.organizationType) === 'Manufacturer',
    );
  },

  // Create organization
  createOrganization(
    data: CreateOrganizationRequestDto,
  ): Promise<CreateOrganizationResponseDto> {
    return api
      .organizationControllerCreateOrganization(
        { createOrganizationRequestDto: data },
        { credentials: 'include' },
      )
      .then((res: unknown) => {
        return res as CreateOrganizationResponseDto;
      });
  },

  // Get organization by ID
  findOrganizationById(id: string): Promise<FindOrganizationDto> {
    return api
      .organizationControllerFindOrganizationById(
        { id },
        { credentials: 'include' },
      )
      .then(
        (organization) =>
          ({
            ...organization,
            externalCsmsConfig: normalizeExternalCsmsConfig(
              organization.externalCsmsConfig ?? null,
            ),
          }) as FindOrganizationDto,
      );
  },

  // delete organization by ID
  deleteOrganizationById(id: string): Promise<void> {
    return api.organizationControllerDeleteOrganization(
      { id },
      { credentials: 'include' },
    );
  },

  // Update organization status
  updateOrganization(
    orgId: string,
    dto: UpdateOrganizationRequestDto,
  ): Promise<UpdateOrganizationResponseDto> {
    return api
      .organizationControllerUpdateOrganization(
        { id: orgId, updateOrganizationRequestDto: dto },
        { credentials: 'include' },
      )
      .then((res: unknown) => {
        return res as UpdateOrganizationResponseDto;
      });
  },
};
